'use client';

import { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Stack, Typography, Button, alpha, keyframes } from '@mui/material';
import {
  Close,
  CheckCircle,
  Info,
  Warning,
  Error as ErrorIcon,
  RocketLaunch,
  LocalShipping,
  Celebration,
} from '@mui/icons-material';

// Keyframe animations
const slideIn = keyframes`
  0% {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  0% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const progressShrink = keyframes`
  0% {
    width: 100%;
  }
  100% {
    width: 0%;
  }
`;

export function NotificationToast({
  open,
  title,
  message,
  details,
  onClose,
  actions,
  severity = 'info',
  position = 'top-right',
  duration = 5000,
  icon: CustomIcon,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsExiting(false);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => handleClose(), duration);
    return () => clearTimeout(timer);
  }, [open, duration, handleClose]);

  const severityConfig = {
    success: {
      icon: CustomIcon || CheckCircle,
      primaryColor: '#10b981',
      secondaryColor: '#34d399',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      bgGradient:
        'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.06) 100%)',
      glowColor: 'rgba(16, 185, 129, 0.4)',
      emoji: '✓',
    },
    info: {
      icon: CustomIcon || RocketLaunch,
      primaryColor: '#6366f1',
      secondaryColor: '#818cf8',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      bgGradient:
        'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.06) 100%)',
      glowColor: 'rgba(99, 102, 241, 0.4)',
      emoji: '🚀',
    },
    warning: {
      icon: CustomIcon || Warning,
      primaryColor: '#f59e0b',
      secondaryColor: '#fbbf24',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      bgGradient:
        'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.06) 100%)',
      glowColor: 'rgba(245, 158, 11, 0.4)',
      emoji: '⚠️',
    },
    error: {
      icon: CustomIcon || ErrorIcon,
      primaryColor: '#ef4444',
      secondaryColor: '#f87171',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      bgGradient:
        'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.06) 100%)',
      glowColor: 'rgba(239, 68, 68, 0.4)',
      emoji: '✕',
    },
    delivery: {
      icon: CustomIcon || LocalShipping,
      primaryColor: '#06b6d4',
      secondaryColor: '#22d3ee',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      bgGradient:
        'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(8, 145, 178, 0.06) 100%)',
      glowColor: 'rgba(6, 182, 212, 0.4)',
      emoji: '📦',
    },
    celebration: {
      icon: CustomIcon || Celebration,
      primaryColor: '#ec4899',
      secondaryColor: '#f472b6',
      gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      bgGradient:
        'linear-gradient(135deg, rgba(236, 72, 153, 0.12) 0%, rgba(219, 39, 119, 0.06) 100%)',
      glowColor: 'rgba(236, 72, 153, 0.4)',
      emoji: '🎉',
    },
  };

  const config = severityConfig[severity] || severityConfig.info;
  const IconComponent = config.icon;

  if (!isVisible) return null;

  // Position styles
  const positionStyles = {
    'top-left': { top: 20, left: 20 },
    'top-right': { top: 20, right: 20 },
    'top-center': { top: 20, left: '50%', transform: 'translateX(-50%)' },
    'bottom-left': { bottom: 100, left: 20 },
    'bottom-right': { bottom: 100, right: 20 },
    'bottom-center': {
      bottom: 100,
      left: '50%',
      transform: 'translateX(-50%)',
    },
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 9999,
        ...positionStyles[position],
        animation: `${isExiting ? slideOut : slideIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)`,
        animationFillMode: 'forwards',
      }}
    >
      <Box
        sx={{
          minWidth: { xs: 320, sm: 380 },
          maxWidth: { xs: 'calc(100vw - 40px)', sm: 440 },
          borderRadius: 4,
          overflow: 'hidden',
          background: '#fff',
          boxShadow: `
            0 0 0 1px ${alpha(config.primaryColor, 0.1)},
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 0 40px ${config.glowColor}
          `,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(config.primaryColor, 0.15)}`,
          position: 'relative',
          '&:hover': {
            animation: `${pulse} 0.3s ease`,
            boxShadow: `
              0 0 0 1px ${alpha(config.primaryColor, 0.2)},
              0 10px 15px -3px rgba(0, 0, 0, 0.1),
              0 25px 30px -5px rgba(0, 0, 0, 0.15),
              0 0 60px ${config.glowColor}
            `,
          },
          transition: 'box-shadow 0.3s ease',
        }}
      >
        {/* Animated shimmer top bar */}
        <Box
          sx={{
            height: 4,
            background: `linear-gradient(
              90deg,
              ${config.primaryColor},
              ${config.secondaryColor},
              ${config.primaryColor}
            )`,
            backgroundSize: '200% 100%',
            animation: `${shimmer} 2s linear infinite`,
          }}
        />

        {/* Progress bar */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: 3,
            background: config.gradient,
            animation: `${progressShrink} ${duration}ms linear forwards`,
            opacity: 0.6,
          }}
        />

        <Stack direction="row" spacing={2} sx={{ p: 2.5, alignItems: 'flex-start' }}>
          {/* Animated Icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              background: config.bgGradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                borderRadius: 'inherit',
                background: config.gradient,
                opacity: 0.15,
                animation: `${pulse} 2s ease-in-out infinite`,
              },
            }}
          >
            <IconComponent
              sx={{
                color: config.primaryColor,
                fontSize: 26,
                position: 'relative',
                zIndex: 1,
              }}
            />
          </Box>

          {/* Content */}
          <Stack spacing={0.75} flex={1} sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: '#0f172a',
                letterSpacing: '-0.01em',
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>

            {message && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: '#64748b',
                  lineHeight: 1.5,
                  pr: 1,
                }}
              >
                {message}
              </Typography>
            )}

            {/* Details list */}
            {Array.isArray(details) && details.length > 0 && (
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2,
                  color: '#64748b',
                  fontSize: 12,
                  '& li': { mb: 0.25 },
                }}
              >
                {details.map((detail, idx) => (
                  <li key={idx}>{detail}</li>
                ))}
              </Box>
            )}

            {/* Actions */}
            {actions && actions.length > 0 && (
              <Stack direction="row" spacing={1} mt={1}>
                {actions.map((action, idx) => (
                  <Button
                    key={idx}
                    onClick={() => {
                      action.onClick?.();
                      handleClose();
                    }}
                    size="small"
                    sx={{
                      background: idx === 0 ? config.gradient : 'transparent',
                      color: idx === 0 ? '#fff' : config.primaryColor,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: 12,
                      borderRadius: 2,
                      px: 2,
                      py: 0.75,
                      border: idx === 0 ? 'none' : `1px solid ${alpha(config.primaryColor, 0.3)}`,
                      '&:hover': {
                        background: idx === 0 ? config.gradient : alpha(config.primaryColor, 0.1),
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Stack>
            )}
          </Stack>

          {/* Close button */}
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: '#94a3b8',
              width: 28,
              height: 28,
              '&:hover': {
                color: '#64748b',
                bgcolor: '#f1f5f9',
              },
            }}
          >
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}

export default NotificationToast;
